import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  View, StyleSheet, TouchableOpacity, Text, Alert, ActivityIndicator,
} from 'react-native';
import MapView, { Marker, Region } from 'react-native-maps';
import * as Location from 'expo-location';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import type { MapStackParamList } from '../../navigation/AppNavigator';
import { useLandmarksStore } from '../../store';
import { landmarksApi } from '../../api';

type Props = NativeStackScreenProps<MapStackParamList, 'Map'>;

export default function MapScreen({ navigation }: Props) {
  const mapRef = useRef<MapView>(null);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const { landmarks, publicLandmarks, setLandmarks, setPublicLandmarks } = useLandmarksStore();

  useEffect(() => {
    requestLocationAndLoad();
  }, []);

  async function requestLocationAndLoad() {
    const { status } = await Location.requestForegroundPermissionsAsync();
    if (status !== 'granted') {
      Alert.alert('Permission denied', 'Location permission is required to use this app.');
      setLoading(false);
      await loadLandmarks(37.7749, -122.4194);
      return;
    }
    try {
      const pos = await Location.getCurrentPositionAsync({ accuracy: Location.Accuracy.High });
      const { latitude: lat, longitude: lng } = pos.coords;
      setUserLocation({ lat, lng });
      setLoading(false);
      await loadLandmarks(lat, lng);
    } catch (err) {
      console.warn('Geolocation error', err);
      setLoading(false);
      await loadLandmarks(37.7749, -122.4194);
    }
  }

  async function loadLandmarks(lat: number, lng: number) {
    try {
      const [mine, pub] = await Promise.all([
        landmarksApi.list(),
        landmarksApi.listPublic(lat, lng),
      ]);
      setLandmarks(mine);
      setPublicLandmarks(pub);
    } catch (e) {
      console.warn('Failed to load landmarks', e);
    }
  }

  function goToMyLocation() {
    if (userLocation && mapRef.current) {
      mapRef.current.animateToRegion({
        latitude: userLocation.lat,
        longitude: userLocation.lng,
        latitudeDelta: 0.01,
        longitudeDelta: 0.01,
      });
    }
  }

  function handleAddAtCurrentLocation() {
    if (!userLocation) return;
    navigation.navigate('AddLandmark', { lat: userLocation.lat, lng: userLocation.lng });
  }

  function handleMapLongPress(e: { nativeEvent: { coordinate: { latitude: number; longitude: number } } }) {
    const { latitude, longitude } = e.nativeEvent.coordinate;
    navigation.navigate('AddLandmark', { lat: latitude, lng: longitude });
  }

  const initialRegion: Region = userLocation
    ? { latitude: userLocation.lat, longitude: userLocation.lng, latitudeDelta: 0.05, longitudeDelta: 0.05 }
    : { latitude: 37.7749, longitude: -122.4194, latitudeDelta: 0.1, longitudeDelta: 0.1 };

  const myIds = new Set(landmarks.map((l) => l.landmarkId));
  const publicOnly = publicLandmarks.filter((l) => !myIds.has(l.landmarkId));

  if (loading) {
    return <View style={styles.loading}><ActivityIndicator size="large" color="#1a73e8" /></View>;
  }

  return (
    <View style={styles.container}>
      <MapView
        ref={mapRef}
        style={styles.map}
        initialRegion={initialRegion}
        showsUserLocation
        onLongPress={handleMapLongPress}
      >
        {landmarks.map((lm) => (
          <Marker
            key={lm.landmarkId}
            coordinate={{ latitude: lm.lat, longitude: lm.lng }}
            title={lm.name}
            description={lm.isPublic ? 'Public' : 'Private'}
            pinColor="#1a73e8"
            onCalloutPress={() => navigation.navigate('LandmarkDetail', { landmarkId: lm.landmarkId })}
          />
        ))}
        {publicOnly.map((lm) => (
          <Marker
            key={lm.landmarkId}
            coordinate={{ latitude: lm.lat, longitude: lm.lng }}
            title={lm.name}
            description="Community landmark"
            pinColor="#34a853"
            onCalloutPress={() => navigation.navigate('LandmarkDetail', { landmarkId: lm.landmarkId })}
          />
        ))}
      </MapView>

      <View style={styles.controls}>
        <TouchableOpacity style={styles.fab} onPress={handleAddAtCurrentLocation}>
          <Text style={styles.fabText}>+</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[styles.fab, styles.fabSecondary]} onPress={goToMyLocation}>
          <Text style={styles.fabTextSecondary}>📍</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[styles.fab, styles.fabSecondary]} onPress={() => navigation.navigate('Landmarks')}>
          <Text style={styles.fabTextSecondary}>☰</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  map: { flex: 1 },
  loading: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  controls: {
    position: 'absolute', bottom: 32, right: 16, gap: 10,
  },
  fab: {
    backgroundColor: '#1a73e8', borderRadius: 24, paddingHorizontal: 16,
    paddingVertical: 10, elevation: 4, alignItems: 'center',
  },
  fabSecondary: { backgroundColor: '#fff' },
  fabText: { color: '#fff', fontWeight: '700', fontSize: 14 },
  fabTextSecondary: { color: '#1a73e8', fontWeight: '700', fontSize: 14 },
});
